import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';

const RentChangeRent = () => {
  const { roomId, rentId } = useParams(); // Support both roomId and rentId
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rentData, setRentData] = useState(null);
  const [formData, setFormData] = useState({
    new_monthly_payment: '',
    effective_date: '',
    reason: '',
    notes: '',
  });
  const dateInputRef = useRef(null);

  useEffect(() => {
    if (roomId || rentId) {
      loadRentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, rentId]);

  const loadRentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Support both roomId and rentId
      // If rentId is provided, we need to get the roomId from the rent first
      let actualRoomId = roomId;
      
      if (rentId && !roomId) {
        // Fetch rent to get roomId
        try {
          const rentResponse = await fetch(`/api/rents/${rentId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (rentResponse.ok) {
            const rentData = await rentResponse.json();
            actualRoomId = rentData.room_id || rentData.room?.id;
            if (!actualRoomId) {
              throw new Error('Could not determine room ID from rent');
            }
          } else {
            throw new Error('Rent not found');
          }
        } catch (err) {
          throw new Error(`Failed to load rent: ${err.message}`);
        }
      }

      if (!actualRoomId) {
        throw new Error('Room ID is required');
      }

      // Laravel: RentsController@editPayment(Room $room)
      // Route: GET /rents/{room}/payments/edit
      // Backend route: /api/rents/:roomId/payments/edit
      const response = await fetch(`/api/rents/${actualRoomId}/payments/edit`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load rent data' }));
        throw new Error(errorData.message || 'Failed to load rent data');
      }

      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'Failed to load rent data');
      }

      // Laravel structure: $room->assignedGuardiansRents
      // Get first rent (Laravel allows multiple guardians per room, but UI shows one)
      // Add null check for data.rents
      if (!data || !data.rents || !Array.isArray(data.rents)) {
        throw new Error('Invalid response format: rents data is missing or invalid');
      }
      
      const firstRent = data.rents.length > 0 ? data.rents[0] : null;
      
      if (!firstRent) {
        throw new Error('No active rent found for this room');
      }

      // Format date for display (Laravel: shouldDisplayDate)
      const formatDate = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && dateString.includes('/')) {
          return dateString; // Already formatted
        }
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      };

      // Get room and property data from backend response
      const roomInfo = data.room || {};
      const propertyInfo = data.property || {};

      // Construct rent data matching Laravel structure
      const rentInfo = {
        id: firstRent.id,
        guardian: {
          id: firstRent.guardian_id,
          unique_guardian_number: firstRent.unique_guardian_number || '',
          fullName: firstRent.guardianName || '',
        },
        room: {
          id: roomInfo?.id || roomId,
          name: roomInfo?.name || 'Room',
        },
        property: propertyInfo, // Safe - already defaulted to {}
        current_monthly_payment: firstRent.license_fee || 0, // Laravel: license_fee is the monthly payment
        date_start: formatDate(firstRent.date_start),
        date_end: formatDate(firstRent.date_end),
      };

      setRentData(rentInfo);
      
      // Initialize form with current monthly payment (license_fee)
      setFormData(prev => ({
        ...prev,
        new_monthly_payment: (firstRent.license_fee || 0).toString(),
      }));

    } catch (err) {
      console.error('❌ [CHANGE_RENT] Error loading rent data:', err);
      setError(err.message);
      toast.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('📝 [CHANGE_RENT] Input changed:', name, '=', value);
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value,
      };
      console.log('📝 [CHANGE_RENT] Updated formData:', newFormData);
      return newFormData;
    });
  };

  // Handle date picker icon click
  const handleCalendarIconClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.() || dateInputRef.current.click();
    }
  };

  // Handle date input change (from native date picker)
  const handleDateChange = (e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      // Convert from yyyy-mm-dd to mm/dd/yyyy
      const [year, month, day] = dateValue.split('-');
      const formattedDate = `${month}/${day}/${year}`;
      setFormData(prev => ({
        ...prev,
        effective_date: formattedDate,
      }));
    }
  };

  // Convert mm/dd/yyyy to yyyy-mm-dd for date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Laravel Parity: UpdatePaymentRequest only validates:
    // - license_fee (monthly_payment maps to this) - required
    // - damage_security_payment - required
    // - pay_gas, pay_electric, pay_water, pay_council_tax - required boolean
    // effective_date, reason, and notes are NOT validated or stored in Laravel
    // Validation
    if (!formData.new_monthly_payment || parseFloat(formData.new_monthly_payment) <= 0) {
      toast.error('New Monthly Payment is required and must be greater than 0');
      return;
    }

    try {
      setSaving(true);

      // Laravel: RentsController@updatePayment(UpdatePaymentRequest)
      // Route: POST /rents/payments/edit
      // Laravel format: payments[guardianId][monthly_payment], payments[guardianId][effective_date], etc.
      const firstRent = rentData;
      
      const monthlyPaymentValue = parseFloat(formData.new_monthly_payment);
      console.log('💰 [CHANGE_RENT] Submitting payment data:', {
        guardianId: firstRent.guardian.id,
        monthly_payment: monthlyPaymentValue,
        effective_date: formData.effective_date,
        reason: formData.reason,
        notes: formData.notes,
        formData: formData
      });

      // Laravel Parity: Only send fields that Laravel UpdatePaymentRequest validates
      // effective_date, reason, and notes are NOT sent (Laravel doesn't use them)
      const paymentData = {
        payments: {
          [firstRent.guardian.id]: {
            license_fee: monthlyPaymentValue, // Laravel expects license_fee, not monthly_payment
            // Note: effective_date, reason, and notes are not sent to match Laravel behavior
          }
        }
      };

      // Use the same roomId logic as loadRentData
      let actualRoomId = roomId;
      if (rentId && !roomId) {
        // Get roomId from rentData if we already have it, otherwise fetch
        if (rentData?.room?.id) {
          actualRoomId = rentData.room.id;
        } else {
          const rentResponse = await fetch(`/api/rents/${rentId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (rentResponse.ok) {
            const rentData = await rentResponse.json();
            actualRoomId = rentData.room_id || rentData.room?.id;
          }
        }
      }

      if (!actualRoomId) {
        throw new Error('Room ID is required');
      }

      const response = await fetch(`/api/rents/${actualRoomId}/payments/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update rent' }));
        throw new Error(errorData.message || 'Failed to update rent');
      }

      const result = await response.json();
      
      if (result.success === false) {
        throw new Error(result.message || 'Failed to update rent');
      }

      toast.success('Rent updated successfully!');
      
      // Laravel redirects to guardians.index
      navigate('/guardians');
      
    } catch (err) {
      console.error('❌ [CHANGE_RENT] Error updating rent:', err);
      toast.error(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Laravel redirects back or to guardians.index
    navigate('/guardians');
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '20px' }}>
        <div className="alert alert-danger">Error: {error}</div>
        <button className="btn btn-default" onClick={handleCancel}>Go Back</button>
      </div>
    );
  }

  if (!rentData) {
    return (
      <div className="container" style={{ padding: '20px' }}>
        <div className="alert alert-warning">No rent data found</div>
        <button className="btn btn-default" onClick={handleCancel}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '20px' }}>
      {/* Laravel: Breadcrumbs::render('property-application.payment.create', $room, $action) */}
      <div className="breadcrumb-row">
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: '14px', color: 'black' }}>
          <a href="/dashboard">Dashboard</a>
        </span>
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: '14px', color: 'black' }}>
          <a href="/rents"> / Rents</a>
        </span>
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: '14px', color: 'black' }}>
          <a href="/rents/after-date-end"> / Placed guardians after date end</a>
        </span>
        <div style={{ background: '#30C693', fontWeight: 400, fontSize: '14px', display: 'inline-block', padding: '2px 8px' }}>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties" style={{ color: 'white' }}>Change Rent</span>
        </div>
      </div>

      {/* Laravel: panel-title title-standard mb-10 */}
      <div className="panel-title title-standard mb-10" style={{ marginBottom: '10px', marginTop: '20px' }}>
        <h3>Change Rent</h3>
      </div>

      {/* Guardian Overview Section - Laravel: Guardian Overview panel */}
      <div className="panel-title title-standard" style={{ marginTop: '20px' }}>
        <h3>Guardian Overview</h3>
      </div>
      <div className="panel-group border-down margin-down" style={{ borderBottom: '1px solid #ddd', marginBottom: '20px', paddingBottom: '20px' }}>
        <div className="col-xs-12 col-sm-6">
          <dl className="list green-list green-list-with-width" style={{ margin: 0 }}>
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>UGD:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.guardian.unique_guardian_number}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>Guardian:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.guardian.fullName}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>Room:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.room.name}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>Property:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.property?.name || rentData.property?.property_id || '-'}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>Current Monthly Payment:</dt>
            <dd style={{ marginLeft: '210px' }}>£{rentData.current_monthly_payment ? parseFloat(rentData.current_monthly_payment).toFixed(2) : '0.00'}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>Start Date:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.date_start}</dd>
            
            <dt style={{ fontWeight: 'bold', width: '200px', float: 'left', clear: 'left' }}>End Date:</dt>
            <dd style={{ marginLeft: '210px' }}>{rentData.date_end}</dd>
          </dl>
        </div>
      </div>

      {/* Change Rent Information Section */}
      <div className="panel-title title-standard" style={{ marginTop: '20px' }}>
        <h3>Change Rent Information</h3>
      </div>
      <form onSubmit={handleSubmit} className="form-horizontal" style={{ marginTop: '20px' }}>
        {/* New Monthly Payment */}
        <div className="form-group">
          <label htmlFor="new_monthly_payment" className="control-label col-sm-3" style={{ fontWeight: 'bold' }}>
            New Monthly Payment (£) <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-9">
            <input
              type="number"
              id="new_monthly_payment"
              name="new_monthly_payment"
              className="form-control"
              value={formData.new_monthly_payment}
              onChange={handleInputChange}
              required
              min="0"
              step="0.01"
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
          </div>
        </div>

        {/* Effective Date - Not stored in Laravel, kept for UI only */}
        <div className="form-group">
          <label htmlFor="effective_date" className="control-label col-sm-3" style={{ fontWeight: 'bold' }}>
            Effective Date
          </label>
          <div className="col-sm-9" style={{ position: 'relative' }}>
            <input
              type="text"
              id="effective_date"
              name="effective_date"
              className="form-control"
              value={formData.effective_date}
              onChange={handleInputChange}
              placeholder="mm/dd/yyyy"
              style={{ width: '100%', padding: '8px', paddingRight: '35px', fontSize: '14px' }}
            />
            {/* Hidden date input for native date picker */}
            <input
              ref={dateInputRef}
              type="date"
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                width: '30px',
                height: '30px',
                cursor: 'pointer',
                zIndex: 1
              }}
              value={convertToDateInputFormat(formData.effective_date)}
              onChange={handleDateChange}
            />
            <span 
              className="glyphicon glyphicon-calendar" 
              onClick={handleCalendarIconClick}
              style={{ 
                position: 'absolute', 
                right: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#999',
                cursor: 'pointer',
                pointerEvents: 'auto',
                zIndex: 2
              }} 
            />
          </div>
        </div>

        {/* Reason for Change - Not stored in Laravel, kept for UI only */}
        <div className="form-group">
          <label htmlFor="reason" className="control-label col-sm-3" style={{ fontWeight: 'bold' }}>
            Reason for Change
          </label>
          <div className="col-sm-9">
            <select
              id="reason"
              name="reason"
              className="form-control"
              value={formData.reason}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            >
              <option value="">Select a reason</option>
              <option value="market_adjustment">Market Adjustment</option>
              <option value="property_improvement">Property Improvement</option>
              <option value="inflation">Inflation</option>
              <option value="contract_renewal">Contract Renewal</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Notes - Not stored in Laravel, kept for UI only */}
        <div className="form-group">
          <label htmlFor="notes" className="control-label col-sm-3" style={{ fontWeight: 'bold' }}>
            Notes
          </label>
          <div className="col-sm-9">
            <textarea
              id="notes"
              name="notes"
              className="form-control"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              placeholder="Additional notes about the rent change..."
              style={{ width: '100%', padding: '8px', fontSize: '14px', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Action Buttons - Laravel: form-group text-right */}
        <div className="form-group text-right" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
          <button
            type="button"
            className="btn btn-default"
            onClick={handleCancel}
            style={{ marginRight: '10px', padding: '8px 20px' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-head btn-standard"
            disabled={saving}
            style={{ 
              background: '#1B2046', 
              color: 'white', 
              padding: '8px 20px',
              opacity: saving ? 0.6 : 1,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Updating...' : 'Update Rent'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RentChangeRent;
